import MediaUtils from '../../utils/media';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IMediaState } from '../../types/interfaces/states';
import { IPlayerError } from '../../types/interfaces/error';
import { PlayerState, TimeShift } from '../../types/enums/player';
import { IRange, ITrack, ITracks } from '../../types/interfaces/player';
import type { RootState } from '../store';

/**
 * Initial state for the media slice.
 * Contains default values for player state, audio/subtitle tracks, timing, and error handling.
 */
const initialState = {
  audio: { language: '', id: '' },
  currentTime: 0,
  bitrate: 0,
  duration: 0,
  error: {
    code: undefined,
    player: undefined,
    retry: 0,
    retryPosition: 0,
    type: undefined,
  },
  live: false,
  vod: false,
  position: 0,
  progressPercentage: 0,
  range: {},
  ready: false,
  speed: 1,
  state: PlayerState.IDLE,
  startover: false,
  subtitles: { language: '', id: '' },
  timeshift: false,
  tracks: {
    audio: [],
    subtitles: [],
  },
  userInteraction: false,
} as IMediaState;

/**
 * Redux slice for managing media player state.
 * Handles player controls (play, pause, seek), track selection, timing, error states, and playback configuration.
 */
const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    clearError: (state) => {
      MediaUtils.clearError(state);
    },
    clearSeek: (state) => {
      MediaUtils.clearSeek(state);
    },
    clearRetryPosition: (state) => {
      MediaUtils.clearRetryPosition(state);
    },
    pause: (state) => {
      MediaUtils.setState(state, PlayerState.PAUSE, true);
    },
    play: (state) => {
      MediaUtils.setState(state, PlayerState.PLAY, true);
    },
    reset: (state) => {
      MediaUtils.reset(state);
    },
    retry: (state) => {
      MediaUtils.retry(state);
    },
    seek: (state, action: PayloadAction<TimeShift | undefined>) => {
      MediaUtils.setSpeed(state, action.payload);
    },
    setAudio: (state, action: PayloadAction<ITrack>) => {
      MediaUtils.setAudio(state, action.payload);
    },
    setBitrate: (state, action: PayloadAction<number>) => {
      MediaUtils.setBitrate(state, action.payload);
    },
    setCurrentTime: (state, action: PayloadAction<number>) => {
      MediaUtils.setCurrentTime(state, action.payload);
    },
    setDuration: (state, action: PayloadAction<number | undefined>) => {
      MediaUtils.setDuration(state, action.payload);
    },
    setProgressPercentage: (state, action: PayloadAction<number>) => {
      state.progressPercentage = action.payload;
    },
    setError: (state, action: PayloadAction<IPlayerError>) => {
      MediaUtils.setError(state, action.payload);
    },
    setLive: (state) => {
      MediaUtils.setLive(state);
    },
    setPosition: (
      state,
      action: PayloadAction<{ position?: number; seek?: boolean }>
    ) => {
      MediaUtils.setPosition(
        state,
        action.payload.position,
        action.payload.seek
      );
    },
    setReady: (state, action: PayloadAction<boolean>) => {
      MediaUtils.setReady(state, action.payload);
    },
    setRange: (state, action: PayloadAction<IRange | undefined>) => {
      MediaUtils.setRange(state, action.payload);
    },
    setStartover: (state, action: PayloadAction<boolean>) => {
      state.startover = action.payload;
    },
    setState: (state, action: PayloadAction<PlayerState>) => {
      MediaUtils.setState(state, action.payload);
    },
    setSubtitles: (state, action: PayloadAction<ITrack>) => {
      MediaUtils.setSubtitles(state, action.payload);
    },
    setTimeshift: (state, action: PayloadAction<boolean>) => {
      MediaUtils.setTimeshift(state, action.payload);
    },
    setTracks: (state, action: PayloadAction<ITracks>) => {
      MediaUtils.setTracks(state, action.payload);
    },
    setVod: (state) => {
      MediaUtils.setVod(state);
    },
    toggle: (state) => {
      MediaUtils.toggle(state);
    },
  },
});

const { actions, reducer: mediaReducer } = mediaSlice;
export const {
  clearError,
  clearRetryPosition,
  clearSeek,
  pause,
  play,
  reset,
  retry,
  seek,
  setAudio,
  setBitrate,
  setCurrentTime,
  setProgressPercentage,
  setDuration,
  setError,
  setLive,
  setPosition,
  setRange,
  setReady,
  setStartover,
  setState,
  setSubtitles,
  setTimeshift,
  setTracks,
  toggle,
  setVod,
} = actions;
export const mediaSelector = (state: RootState) => state.media;
export default mediaReducer;
